import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Plus,
  Globe,
  Calendar,
  AlertTriangle,
  ArrowRight,
  Lock,
  LayoutGrid,
  List,
  Play,
  Loader2,
  FileCode,
  TrendingUp,
  BarChart3,
  Heart,
  Bell,
} from 'lucide-react'
import { toast } from 'sonner'
import { useSession } from '@/client/lib/auth'
import { useSites, useCreateSite, useCreateSiteCrawl, usePortfolioStats, type Site } from '@/client/modules/site/hooks/useSites'
import { scoreColor } from '@/client/lib/colors'

// ── Types ─────────────────────────────────────────────────────

type FilterKey = 'all' | 'managed' | 'free' | 'issues' | 'never-crawled' | 'stale' | 'wordpress'
type SortKey = 'managed-first' | 'domain' | 'score-asc' | 'score-desc' | 'last-crawled' | 'most-issues'
type ViewMode = 'grid' | 'list'

// ── Helpers ───────────────────────────────────────────────────

function formatDate(iso: string | null) {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function frequencyLabel(f: string | null) {
  if (!f) return null
  return { daily: 'Daily', weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' }[f] || f
}


function filterSites(sites: Site[], filter: FilterKey): Site[] {
  switch (filter) {
    case 'managed': return sites.filter(s => s.tier === 'managed')
    case 'free': return sites.filter(s => s.tier === 'free')
    case 'issues': return sites.filter(s => (s.latest_issues_count ?? 0) > 0)
    case 'never-crawled': return sites.filter(s => !s.latest_crawl_at)
    case 'stale': return sites.filter(s => {
      if (!s.latest_crawl_at) return false
      const daysSince = (Date.now() - new Date(s.latest_crawl_at).getTime()) / 86400000
      return daysSince > 30
    })
    case 'wordpress': return sites.filter(s => !!s.is_wordpress)
    default: return sites
  }
}

function sortSites(sites: Site[], sort: SortKey): Site[] {
  return [...sites].sort((a, b) => {
    switch (sort) {
      case 'managed-first': {
        if (a.tier !== b.tier) return a.tier === 'managed' ? -1 : 1
        return a.domain.localeCompare(b.domain)
      }
      case 'domain':
        return a.domain.localeCompare(b.domain)
      case 'score-asc':
        return (a.latest_avg_seo_score ?? -1) - (b.latest_avg_seo_score ?? -1)
      case 'score-desc':
        return (b.latest_avg_seo_score ?? -1) - (a.latest_avg_seo_score ?? -1)
      case 'last-crawled': {
        const aTime = a.latest_crawl_at ? new Date(a.latest_crawl_at).getTime() : 0
        const bTime = b.latest_crawl_at ? new Date(b.latest_crawl_at).getTime() : 0
        return bTime - aTime
      }
      case 'most-issues':
        return (b.latest_issues_count ?? 0) - (a.latest_issues_count ?? 0)
      default:
        return 0
    }
  })
}

// ── Create Site Dialog ────────────────────────────────────────

function CreateSiteDialog() {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState('')
  const [frequency, setFrequency] = useState('weekly')
  const [maxPages, setMaxPages] = useState(500)
  const [alertEmail, setAlertEmail] = useState('')
  const navigate = useNavigate()
  const createSite = useCreateSite()

  const handleCreate = async () => {
    try {
      const result = await createSite.mutateAsync({
        url: url.startsWith('http') ? url : `https://${url}`,
        frequency,
        maxPages,
        rateLimit: 2,
        respectRobots: false,
        alertEmail: alertEmail || undefined,
      })
      setUrl('')
      setFrequency('weekly')
      setMaxPages(500)
      setAlertEmail('')
      setOpen(false)
      navigate(`/dashboard/sites/${result.id}`)
    } catch {
      toast.error('Failed to create site')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Site
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Site</DialogTitle>
          <DialogDescription>
            Add a website to monitor. You can set up automatic crawls.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="url">Website URL</Label>
            <Input
              id="url"
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="frequency">Schedule Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="fortnightly">Fortnightly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="maxPages">Max Pages</Label>
            <Input
              id="maxPages"
              type="number"
              min={10}
              max={5000}
              value={maxPages}
              onChange={(e) => setMaxPages(Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="alertEmail">Alert Email (optional)</Label>
            <Input
              id="alertEmail"
              type="email"
              placeholder="you@example.com"
              value={alertEmail}
              onChange={(e) => setAlertEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Receive an email after each crawl completes.</p>
          </div>
        </div>
        {createSite.isError && (
          <p className="text-sm text-destructive">Failed to create site. The domain may already exist.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={!url || createSite.isPending}>
            {createSite.isPending ? 'Creating...' : 'Add Site'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Managed Badge ─────────────────────────────────────────────

function TierBadge({ tier }: { tier: string }) {
  if (tier !== 'managed') return null
  return (
    <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 text-[10px] px-1.5 py-0">
      Managed
    </Badge>
  )
}

// ── Site Card (Grid View) ─────────────────────────────────────

function SiteCard({ site, currentUserId }: { site: Site; currentUserId?: string }) {
  const score = site.latest_avg_seo_score != null ? Math.round(site.latest_avg_seo_score) : null
  const freq = frequencyLabel(site.schedule_frequency)
  const isOwnSite = site.created_by === currentUserId
  const navigate = useNavigate()
  const createCrawl = useCreateSiteCrawl(site.id)

  const handleCrawlNow = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const result = await createCrawl.mutateAsync()
      toast.success(`Crawl started for ${site.domain}`)
      navigate(`/dashboard/crawls/${result.id}`)
    } catch {
      toast.error('Failed to start crawl')
    }
  }

  return (
    <Link to={`/dashboard/sites/${site.id}`}>
      <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
              <CardTitle className="text-base truncate">{site.domain}</CardTitle>
              <TierBadge tier={site.tier} />
              {!!site.is_wordpress && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700">
                  <FileCode className="h-2.5 w-2.5 mr-0.5" />
                  WP
                </Badge>
              )}
              {!!site.is_private && (
                <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-2">
              {site.latest_health_score != null && (
                <span className={`text-xs font-medium ${scoreColor(site.latest_health_score)}`} title={`Health: ${site.latest_health_score}%`}>
                  {site.latest_health_score}%
                </span>
              )}
              {score !== null && (
                <span className={`text-lg font-bold ${scoreColor(score)}`}>
                  {score}
                </span>
              )}
            </div>
          </div>
          <CardDescription className="truncate">
            {site.url}
            {!isOwnSite && site.creator_name && (
              <span className="ml-2 text-xs opacity-70">by {site.creator_name}</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              {site.schedule_enabled && freq ? (
                <Badge variant="outline" className="text-xs">
                  <Calendar className="mr-1 h-3 w-3" />
                  {freq}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">No schedule</Badge>
              )}
              {(site.latest_issues_count ?? 0) > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {site.latest_issues_count} issue{site.latest_issues_count !== 1 ? 's' : ''}
                </span>
              )}
              {site.estimated_traffic != null && site.estimated_traffic > 0 && (
                <span className="text-xs text-muted-foreground flex items-center gap-1" title="Estimated monthly organic traffic">
                  <TrendingUp className="h-3 w-3" />
                  {site.estimated_traffic >= 1000
                    ? `${(site.estimated_traffic / 1000).toFixed(1)}k`
                    : Math.round(site.estimated_traffic)} ETV
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-1.5 text-xs text-muted-foreground hover:text-primary"
                onClick={handleCrawlNow}
                disabled={createCrawl.isPending}
              >
                {createCrawl.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
              </Button>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
            </div>
          </div>
          {site.latest_crawl_at && (
            <p className="text-xs text-muted-foreground mt-2">
              Last crawl: {formatDate(site.latest_crawl_at)}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

// ── Site Table (List View) ────────────────────────────────────

function SiteTable({ sites, currentUserId }: { sites: Site[]; currentUserId?: string }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Domain</TableHead>
            <TableHead className="w-20">Tier</TableHead>
            <TableHead className="w-16 text-right">Score</TableHead>
            <TableHead className="w-16 text-right">Issues</TableHead>
            <TableHead className="w-16 text-right">Pages</TableHead>
            <TableHead className="w-24">Schedule</TableHead>
            <TableHead className="w-36">Last Crawl</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sites.map((site) => {
            const score = site.latest_avg_seo_score != null ? Math.round(site.latest_avg_seo_score) : null
            const freq = frequencyLabel(site.schedule_frequency)
            const isOwnSite = site.created_by === currentUserId

            return (
              <TableRow key={site.id} className="cursor-pointer">
                <TableCell>
                  <Link to={`/dashboard/sites/${site.id}`} className="flex items-center gap-2 min-w-0">
                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium truncate">{site.domain}</span>
                    {!!site.is_private && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                    {!isOwnSite && site.creator_name && (
                      <span className="text-xs text-muted-foreground opacity-70 shrink-0">by {site.creator_name}</span>
                    )}
                  </Link>
                </TableCell>
                <TableCell>
                  <TierBadge tier={site.tier} />
                </TableCell>
                <TableCell className="text-right">
                  {score !== null ? (
                    <span className={`font-bold ${scoreColor(score)}`}>{score}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {(site.latest_issues_count ?? 0) > 0 ? (
                    <span className="flex items-center justify-end gap-1 text-muted-foreground">
                      <AlertTriangle className="h-3 w-3" />
                      {site.latest_issues_count}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right text-muted-foreground">
                  {site.latest_pages_crawled ?? '-'}
                </TableCell>
                <TableCell>
                  {site.schedule_enabled && freq ? (
                    <Badge variant="outline" className="text-xs">
                      {freq}
                    </Badge>
                  ) : (
                    <span className="text-xs text-muted-foreground">None</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {formatDate(site.latest_crawl_at)}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

// ── Toolbar ───────────────────────────────────────────────────

function SitesToolbar({
  sites,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  search,
  onSearchChange,
}: {
  sites: Site[]
  filter: FilterKey
  onFilterChange: (f: FilterKey) => void
  sort: SortKey
  onSortChange: (s: SortKey) => void
  view: ViewMode
  onViewChange: (v: ViewMode) => void
  search: string
  onSearchChange: (s: string) => void
}) {
  const counts: Record<FilterKey, number> = {
    all: sites.length,
    managed: sites.filter(s => s.tier === 'managed').length,
    free: sites.filter(s => s.tier === 'free').length,
    issues: sites.filter(s => (s.latest_issues_count ?? 0) > 0).length,
    'never-crawled': sites.filter(s => !s.latest_crawl_at).length,
    stale: sites.filter(s => {
      if (!s.latest_crawl_at) return false
      return (Date.now() - new Date(s.latest_crawl_at).getTime()) / 86400000 > 30
    }).length,
    wordpress: sites.filter(s => !!s.is_wordpress).length,
  }

  const filters: { key: FilterKey; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'managed', label: 'Managed' },
    { key: 'free', label: 'Free' },
    { key: 'issues', label: 'Issues' },
    { key: 'never-crawled', label: 'Never Crawled' },
    { key: 'stale', label: 'Stale' },
    { key: 'wordpress', label: 'WordPress' },
  ]

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <ToggleGroup
        type="single"
        value={filter}
        onValueChange={(v: string) => v && onFilterChange(v as FilterKey)}
        variant="outline"
        size="sm"
        className="flex-wrap"
      >
        {filters.map(({ key, label }) => (
          <ToggleGroupItem key={key} value={key} className="text-xs gap-1.5">
            {label}
            {counts[key] > 0 && (
              <span className="text-muted-foreground">{counts[key]}</span>
            )}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="flex items-center gap-2 sm:ml-auto">
        <Input
          type="search"
          placeholder="Search domains..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 w-[200px] text-xs"
        />
        <Select value={sort} onValueChange={(v) => onSortChange(v as SortKey)}>
          <SelectTrigger className="w-[160px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="managed-first">Managed First</SelectItem>
            <SelectItem value="domain">Domain A-Z</SelectItem>
            <SelectItem value="score-asc">Score (low to high)</SelectItem>
            <SelectItem value="score-desc">Score (high to low)</SelectItem>
            <SelectItem value="last-crawled">Last Crawled</SelectItem>
            <SelectItem value="most-issues">Most Issues</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex border rounded-md">
          <Button
            variant={view === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-r-none"
            onClick={() => onViewChange('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-8 w-8 rounded-l-none"
            onClick={() => onViewChange('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Portfolio Summary ─────────────────────────────────────────

function PortfolioSummary() {
  const { data: portfolio, isLoading } = usePortfolioStats()
  if (isLoading || !portfolio) return null
  const { stats } = portfolio

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <div className="rounded-md border p-3">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <Globe className="h-3.5 w-3.5" />
          <span className="text-xs">Managed Sites</span>
        </div>
        <div className="text-xl font-bold">{stats.managed_count}</div>
      </div>
      <div className="rounded-md border p-3">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <BarChart3 className="h-3.5 w-3.5" />
          <span className="text-xs">Avg SEO Score</span>
        </div>
        <div className={`text-xl font-bold ${stats.overall_avg_score != null ? scoreColor(stats.overall_avg_score) : ''}`}>
          {stats.overall_avg_score != null ? Math.round(stats.overall_avg_score) : 'N/A'}
        </div>
      </div>
      <div className="rounded-md border p-3">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <Heart className="h-3.5 w-3.5" />
          <span className="text-xs">Pages 2xx</span>
        </div>
        <div className={`text-xl font-bold ${stats.avg_health_score != null ? scoreColor(stats.avg_health_score) : ''}`}>
          {stats.avg_health_score != null ? `${Math.round(stats.avg_health_score)}%` : 'N/A'}
        </div>
      </div>
      <div className="rounded-md border p-3">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <Bell className="h-3.5 w-3.5" />
          <span className="text-xs">Active Alerts</span>
        </div>
        <div className={`text-xl font-bold ${portfolio.unacknowledgedAlerts > 0 ? 'text-destructive' : ''}`}>
          {portfolio.unacknowledgedAlerts}
        </div>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────

export function SitesPage() {
  const { data: session } = useSession()
  const { data, isLoading } = useSites()
  const sites = data?.sites ?? []
  const [searchParams] = useSearchParams()
  const initialFilter = (searchParams.get('filter') as FilterKey) || 'all'

  const [filter, setFilter] = useState<FilterKey>(initialFilter)
  const [sort, setSort] = useState<SortKey>('managed-first')
  const [view, setView] = useState<ViewMode>('grid')
  const [search, setSearch] = useState('')

  const trimmedSearch = search.trim().toLowerCase()
  const filtered = trimmedSearch
    ? filterSites(sites, filter).filter((s) => s.domain.toLowerCase().includes(trimmedSearch))
    : filterSites(sites, filter)
  const sorted = sortSites(filtered, sort)
  const currentUserId = session?.user?.id

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Sites</h1>
          <p className="text-sm text-muted-foreground">Manage your monitored websites</p>
        </div>
        <CreateSiteDialog />
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : sites.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Globe className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No sites yet</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
              Add a website to start monitoring its SEO health.
            </p>
            <CreateSiteDialog />
          </CardContent>
        </Card>
      ) : (
        <>
          <SitesToolbar
            sites={sites}
            filter={filter}
            onFilterChange={setFilter}
            sort={sort}
            onSortChange={setSort}
            view={view}
            onViewChange={setView}
            search={search}
            onSearchChange={setSearch}
          />

          {(filter === 'all' || filter === 'managed') && <PortfolioSummary />}

          <p className="text-xs text-muted-foreground">
            {filtered.length === sites.length
              ? `${sites.length} sites`
              : `Showing ${filtered.length} of ${sites.length} sites`}
          </p>

          {sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No sites match this filter.
            </p>
          ) : view === 'grid' ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sorted.map((site) => (
                <SiteCard key={site.id} site={site} currentUserId={currentUserId} />
              ))}
            </div>
          ) : (
            <SiteTable sites={sorted} currentUserId={currentUserId} />
          )}
        </>
      )}
    </div>
  )
}
